/* =========================================================================
   Praesidus — Ranger landing page
   Dependency-free, instance-safe behaviour for the ranger-* sections:

     1. shared signup submission (Klaviyo client subscriptions)
     2. colourway variant switching, independent per model
     3. persistent signup bar visibility, driven by IntersectionObserver
     4. campaign video poster -> playback

   Every root is discovered by a data- attribute and marked once, so re-running
   boot() is idempotent. shopify:section:load / :unload are handled so the
   Theme Editor can reload, reorder and remove sections freely, and every
   observer a section owns is disconnected when that section unloads.

   No global state, no duplicate listeners, no scroll handlers, no logs.
   ========================================================================= */
(function () {
  'use strict';

  /* Every ranger-* section renders the <script> tag, so the page can carry a
     dozen references to this file. The browser fetches it once but executes
     each <script> element separately — without this guard each execution
     would build its own observers and add a second submit/click listener to
     every form and swatch. One namespaced flag, set on the first run. */
  if (window.rangerLandingPageBooted) return;
  window.rangerLandingPageBooted = true;

  /* Per-element bookkeeping without leaking globals or expando collisions. */
  var bound = new WeakSet();
  var teardown = new WeakMap();

  function reduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function onUnload(root, fn) {
    var list = teardown.get(root) || [];
    list.push(fn);
    teardown.set(root, list);
  }

  /* --------------------------------------------- SCROLL TO FORM --------- */
  /* Smooth-scrolls to the final signup band and hands over focus once the
     scroll has settled, without a second jump. Shared by the persistent bar
     and any [data-ranger-scroll-to-form] control. */
  function scrollToFinalSignup(event) {
    var target = document.querySelector('[data-ranger-final-signup]');
    if (!target) return; /* section removed: let the href behave natively */
    if (event) event.preventDefault();

    var input = target.querySelector('[data-ranger-signup-input]');
    target.scrollIntoView({ behavior: reduced() ? 'auto' : 'smooth', block: 'start' });
    if (!input) return;
    if (reduced()) { input.focus(); return; }

    /* Wait for the smooth scroll to settle, then focus without re-scrolling.
       requestAnimationFrame alone is not enough: it is paused in a background
       or occluded tab, so a visitor who clicks and switches away would never
       receive focus. A bounded timer backs it up, and `done` keeps whichever
       fires first from focusing twice. */
    var settled = 0;
    var last = null;
    var done = false;

    function land() {
      if (done) return;
      done = true;
      input.focus({ preventScroll: true });
    }

    (function watch() {
      if (done) return;
      var y = Math.round(target.getBoundingClientRect().top);
      settled = y === last ? settled + 1 : 0;
      last = y;
      if (settled > 2) { land(); return; }
      requestAnimationFrame(watch);
    })();

    setTimeout(land, 700);
  }

  function bindScrollToForm(el) {
    if (bound.has(el)) return;
    bound.add(el);
    el.addEventListener('click', scrollToFinalSignup);
    onUnload(el, function () { el.removeEventListener('click', scrollToFinalSignup); });
  }

  /* ------------------------------------------------ CONVERSION ---------- */
  /* Fired ONLY from a confirmed Klaviyo success, never on click or on a failed
     request, so a Lead always corresponds to a stored profile.

     One Lead per visitor per session by default: someone who signs up in the
     hero and again in the launch band is one person, and double-counting
     inflates the metric Meta optimises ad delivery against. The first event
     still carries content_category, so hero vs launch stays attributable.
     Set LEAD_ONCE_PER_SESSION = false to record every submission instead. */
  var LEAD_ONCE_PER_SESSION = true;
  var LEAD_SESSION_KEY = 'ranger-lp-lead-sent';

  function leadAlreadySent() {
    if (!LEAD_ONCE_PER_SESSION) return false;
    try {
      return window.sessionStorage.getItem(LEAD_SESSION_KEY) === '1';
    } catch (e) {
      /* Private mode / blocked storage: fall through and fire. */
      return false;
    }
  }

  function markLeadSent() {
    try { window.sessionStorage.setItem(LEAD_SESSION_KEY, '1'); } catch (e) {}
  }

  /* Shopify's Customer Privacy API is the source of truth for marketing
     consent where it applies. If it is present and says no, the pixel is
     skipped; if it is not on the page at all, there is no consent regime to
     honour and the event fires. */
  function marketingConsentGranted() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.marketingAllowed === 'function') return cp.marketingAllowed();
    } catch (e) {}
    return true;
  }

  function trackSignupSuccess(form, email) {
    var source = form.getAttribute('data-source') || 'ranger-landing-page';

    /* Klaviyo onsite: tie the browser session to the profile so browse and
       abandon flows can pick this visitor up. Not consent-gated here -
       Klaviyo already holds the subscription the visitor just opted into. */
    try {
      if (window._learnq && typeof window._learnq.push === 'function') {
        window._learnq.push(['identify', { $email: email }]);
      }
    } catch (e) {}

    if (leadAlreadySent()) return;
    if (!marketingConsentGranted()) return;

    try {
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead', {
          content_name: 'Ranger launch signup',
          content_category: source
        });
        markLeadSent();
      }
    } catch (e) {}
  }

  /* ======================================================= 1. SIGNUP ===== */
  /* One handler for every Ranger form, so the hero and the final launch slab
     validate, load, succeed and fail identically. */
  function bindSignup(form) {
    if (bound.has(form)) return;
    bound.add(form);

    var field = form.querySelector('[data-ranger-signup-field]');
    var input = form.querySelector('[data-ranger-signup-input]');
    var button = form.querySelector('[data-ranger-signup-button]');
    var note = form.querySelector('[data-ranger-signup-note]');
    if (!input) return;

    var idle = note ? (note.getAttribute('data-idle') || '') : '';
    var busy = false;

    function say(message, state) {
      if (!note) return;
      note.textContent = message || '';
      note.className =
          'ranger-lp__signup-note' + (state ? ' ranger-lp__signup-note--' + state : '');
    }

    function setBusy(next) {
      busy = next;
      form.setAttribute('aria-busy', next ? 'true' : 'false');
      if (button) button.disabled = next;
      input.readOnly = next;
    }

    function onSubmit(event) {
      event.preventDefault();
      if (busy) return; /* duplicate-submission guard */

      var email = (input.value || '').trim();
      if (!email || !input.checkValidity()) {
        input.setAttribute('aria-invalid', 'true');
        say(form.getAttribute('data-invalid') || 'Please enter a valid email address.', 'error');
        input.focus();
        return;
      }
      input.removeAttribute('aria-invalid');

      setBusy(true);
      say(form.getAttribute('data-pending') || 'Adding you to the list…', 'pending');

      var endpoint =
          'https://a.klaviyo.com/client/subscriptions/?company_id=' +
          encodeURIComponent(form.getAttribute('data-company-id') || '');

      fetch(endpoint, {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          revision: form.getAttribute('data-revision') || '2024-10-15'
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: form.getAttribute('data-source') || 'ranger-landing-page',
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email: email }
                }
              }
            },
            relationships: {
              list: { data: { type: 'list', id: form.getAttribute('data-list-id') || '' } }
            }
          }
        })
      })
          .then(function (response) {
            if (!response.ok) throw new Error('Klaviyo responded ' + response.status);
            /* Success is terminal: retire the field and keep the message. */
            if (field) field.hidden = true;
            say(form.getAttribute('data-success'), 'success');
            if (note) note.focus && note.setAttribute('tabindex', '-1');
            trackSignupSuccess(form, email);
          })
          .catch(function () {
            say(form.getAttribute('data-error'), 'error');
            setBusy(false);
            input.focus();
          });
    }

    function onInput() {
      if (note && note.className !== 'ranger-lp__signup-note') say(idle);
      input.removeAttribute('aria-invalid');
    }

    form.addEventListener('submit', onSubmit);
    input.addEventListener('input', onInput);

    onUnload(form, function () {
      form.removeEventListener('submit', onSubmit);
      input.removeEventListener('input', onInput);
    });
  }

  /* ====================================================== 2. VARIANTS ==== */
  /* Each model root owns its own state, so Ana-Digi and GMT never interfere.
     Panels are pre-rendered in the markup and swapped by [hidden], which keeps
     the copy in the DOM for search engines and costs no layout shift. */
  function bindModel(root) {
    if (bound.has(root)) return;
    bound.add(root);

    var buttons = Array.prototype.slice.call(
        root.querySelectorAll('[data-ranger-variant-button]')
    );
    var panels = Array.prototype.slice.call(
        root.querySelectorAll('[data-ranger-variant-panel]')
    );
    if (!buttons.length || !panels.length) return;

    function select(key, moveFocus) {
      for (var i = 0; i < panels.length; i++) {
        var isActive = panels[i].getAttribute('data-variant-key') === key;
        panels[i].hidden = !isActive;
        /* Alternate imagery is only fetched once its panel is first shown. */
        if (isActive) {
          var lazy = panels[i].querySelectorAll('img[data-ranger-src]');
          for (var l = 0; l < lazy.length; l++) {
            var img = lazy[l];
            var srcset = img.getAttribute('data-ranger-srcset');
            if (srcset) img.setAttribute('srcset', srcset);
            img.setAttribute('src', img.getAttribute('data-ranger-src'));
            img.removeAttribute('data-ranger-src');
            img.removeAttribute('data-ranger-srcset');
          }
        }
      }
      for (var b = 0; b < buttons.length; b++) {
        var on = buttons[b].getAttribute('data-variant-key') === key;
        buttons[b].setAttribute('aria-pressed', on ? 'true' : 'false');
        buttons[b].classList.toggle('is-active', on);
        if (on && moveFocus) buttons[b].focus();
      }
      root.setAttribute('data-active-variant', key);
    }

    function onClick(event) {
      var button = event.currentTarget;
      select(button.getAttribute('data-variant-key'), false);
    }

    /* Roving arrow-key navigation across the colourway row. */
    function onKeydown(event) {
      var index = buttons.indexOf(event.currentTarget);
      if (index < 0) return;
      var next = null;
      if (event.key === 'ArrowRight' || event.key === 'ArrowDown') next = index + 1;
      else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') next = index - 1;
      else if (event.key === 'Home') next = 0;
      else if (event.key === 'End') next = buttons.length - 1;
      if (next === null) return;
      event.preventDefault();
      next = (next + buttons.length) % buttons.length;
      select(buttons[next].getAttribute('data-variant-key'), true);
    }

    for (var i = 0; i < buttons.length; i++) {
      buttons[i].addEventListener('click', onClick);
      buttons[i].addEventListener('keydown', onKeydown);
    }

    /* Honour the initial active block; fall back to the first if a merchant
       marked several — or none — as active. */
    var initial =
        root.getAttribute('data-initial-variant') ||
        (buttons[0] && buttons[0].getAttribute('data-variant-key'));
    if (initial) select(initial, false);

    onUnload(root, function () {
      for (var i = 0; i < buttons.length; i++) {
        buttons[i].removeEventListener('click', onClick);
        buttons[i].removeEventListener('keydown', onKeydown);
      }
    });
  }

  /* ==================================================== 3. PERSISTENT ==== */
  /* Visible only in the middle of the page: below the hero, above the final
     signup. Both landmarks are found by data attribute rather than DOM order,
     so a merchant may reorder sections; if either is absent the bar degrades
     to "show once the hero is gone" / "never hide", with no errors.

     State is derived from the observers alone — never from scroll position —
     so both directions behave the same and the bar cannot flicker. */
  function bindPersistent(bar) {
    if (bound.has(bar)) return;
    bound.add(bar);

    var hero = document.querySelector('[data-ranger-hero]');
    var final = document.querySelector('[data-ranger-final-signup]');

    var pastHero = !hero; /* no hero on the page -> treat it as already passed */
    var finalVisible = false;
    var observers = [];

    function apply() {
      var show = pastHero && !finalVisible;
      bar.hidden = !show;
      bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    }

    if ('IntersectionObserver' in window) {
      if (hero) {
        var heroObserver = new IntersectionObserver(function (entries) {
          var entry = entries[entries.length - 1];
          /* Past the hero only when it has left upwards. */
          pastHero = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
          apply();
        });
        heroObserver.observe(hero);
        observers.push(heroObserver);
      }

      if (final) {
        var finalObserver = new IntersectionObserver(
            function (entries) {
              var entry = entries[entries.length - 1];
              finalVisible = entry.isIntersecting;
              apply();
            },
            { threshold: 0.1 }
        );
        finalObserver.observe(final);
        observers.push(finalObserver);
      }
    } else {
      /* No IntersectionObserver: show the bar rather than hide it forever. */
      pastHero = true;
    }

    apply();

    /* The CTA scrolls to the final form and hands over focus on arrival. */
    var cta = bar.querySelector('[data-ranger-persistent-cta]');
    var onCta = scrollToFinalSignup;
    if (cta) cta.addEventListener('click', onCta);

    onUnload(bar, function () {
      for (var i = 0; i < observers.length; i++) observers[i].disconnect();
      if (cta) cta.removeEventListener('click', onCta);
    });
  }

  /* ========================================================= 4. VIDEO ==== */
  /* Poster until asked. Native controls take over on play, and the poster is
     removed from the accessibility tree rather than the layout, so the frame
     keeps its aspect ratio and nothing shifts. */
  function bindVideo(root) {
    if (bound.has(root)) return;
    bound.add(root);

    var video = root.querySelector('video');
    var embed = root.querySelector('[data-ranger-video-embed]');
    var trigger = root.querySelector('[data-ranger-video-play]');
    if (!trigger) return;

    function start(event) {
      event.preventDefault();
      root.setAttribute('data-playing', 'true');

      if (video) {
        video.controls = true;
        var played = video.play();
        if (played && typeof played.catch === 'function') {
          /* Autoplay refusal is not an error worth surfacing: controls are up. */
          played.catch(function () {});
        }
        video.focus({ preventScroll: true });
        return;
      }

      /* External embed: build the iframe only now, so no third-party frame
         loads until the visitor asks for the film. */
      if (embed && !embed.firstChild) {
        var src = embed.getAttribute('data-src') || '';
        var frame = document.createElement('iframe');
        frame.setAttribute('src', src + (src.indexOf('?') < 0 ? '?' : '&') + 'autoplay=1');
        frame.setAttribute('title', embed.getAttribute('data-title') || 'Campaign film');
        frame.setAttribute('allow', 'accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture');
        frame.setAttribute('allowfullscreen', 'allowfullscreen');
        frame.setAttribute('loading', 'lazy');
        embed.appendChild(frame);
        frame.focus({ preventScroll: true });
      }
    }

    trigger.addEventListener('click', start);

    onUnload(root, function () {
      trigger.removeEventListener('click', start);
      if (video) {
        video.pause();
        /* Release the buffer so an unloaded section stops downloading. */
        video.removeAttribute('src');
        video.load();
      }
      /* Drop the third-party frame so it stops playing and is not orphaned. */
      if (embed) embed.textContent = '';
      root.removeAttribute('data-playing');
    });
  }

  /* ========================================================== WIRING ===== */
  /* -------------------------------------------------------------------------
     v2 band 01 — anatomy

     Every panel is already in the document; this only moves `hidden` and
     aria-pressed between them. Markers on the photograph and the name chips
     below it are two controls for one selection, so both are updated together.

     Left/right arrows move between markers the way a radio group does, which
     is what a group of mutually exclusive controls should feel like.
     ------------------------------------------------------------------------- */
  function bindAnatomy(root) {
    if (bound.has(root)) return;
    bound.add(root);

    var pins = Array.prototype.slice.call(root.querySelectorAll('[data-ranger-anat-pin]'));
    var chips = Array.prototype.slice.call(root.querySelectorAll('[data-ranger-anat-chip]'));
    var panels = Array.prototype.slice.call(root.querySelectorAll('[data-ranger-anat-panel]'));
    if (!panels.length) return;

    function select(index, moveFocus) {
      for (var p = 0; p < panels.length; p++) {
        panels[p].hidden = panels[p].getAttribute('data-ranger-anat-panel') !== String(index);
      }
      for (var i = 0; i < pins.length; i++) {
        pins[i].setAttribute('aria-pressed', pins[i].getAttribute('data-ranger-anat-pin') === String(index) ? 'true' : 'false');
      }
      for (var c = 0; c < chips.length; c++) {
        chips[c].setAttribute('aria-pressed', chips[c].getAttribute('data-ranger-anat-chip') === String(index) ? 'true' : 'false');
      }
      if (moveFocus && pins[index]) pins[index].focus();
    }

    function onClick(event) {
      var el = event.currentTarget;
      select(Number(el.getAttribute(el.hasAttribute('data-ranger-anat-pin')
        ? 'data-ranger-anat-pin' : 'data-ranger-anat-chip')), false);
    }

    function onKeydown(event) {
      var key = event.key;
      if (key !== 'ArrowRight' && key !== 'ArrowLeft' && key !== 'Home' && key !== 'End') return;
      event.preventDefault();
      var el = event.currentTarget;
      var index = Number(el.getAttribute(el.hasAttribute('data-ranger-anat-pin')
        ? 'data-ranger-anat-pin' : 'data-ranger-anat-chip'));
      var last = panels.length - 1;
      var next = key === 'Home' ? 0
        : key === 'End' ? last
        : key === 'ArrowRight' ? (index + 1 > last ? 0 : index + 1)
        : (index - 1 < 0 ? last : index - 1);
      select(next, true);
    }

    var controls = pins.concat(chips);
    for (var i = 0; i < controls.length; i++) {
      controls[i].addEventListener('click', onClick);
      controls[i].addEventListener('keydown', onKeydown);
    }

    select(0, false);

    onUnload(root, function () {
      for (var i = 0; i < controls.length; i++) {
        controls[i].removeEventListener('click', onClick);
        controls[i].removeEventListener('keydown', onKeydown);
      }
    });
  }

  /* -------------------------------------------------------------------------
     v2 band 02 — the Ana-Digi display

     A seven-segment simulation. The digits are markup; this only toggles
     `is-on` per segment. Every string other than the computed values comes
     off the mode button's data attributes, so the copy stays in the theme.

     Timing is the part worth reading. The mockup this came from runs
     setInterval(render, 50) for the life of the page — twenty renders a
     second, forever, on a band that is usually off screen. Here:
       · the clock ticks 4x a second, which is enough for a seconds digit
       · the chronograph switches to requestAnimationFrame while running, so
         the 1/100 digit is smooth and the browser stops it in a hidden tab
       · everything stops when the band leaves the viewport
     ------------------------------------------------------------------------- */
  var SEGMENTS = {
    '0': 'abcdef', '1': 'bc', '2': 'abdeg', '3': 'abcdg', '4': 'bcfg',
    '5': 'acdfg', '6': 'acdefg', '7': 'abc', '8': 'abcdefg', '9': 'abcdfg',
    '-': 'g', ' ': '', 'A': 'abcefg', 'C': 'adef', 'E': 'adefg', 'F': 'aefg',
    'H': 'bcefg', 'L': 'def', 'P': 'abefg', 'U': 'bcdef',
    'o': 'cdeg', 'n': 'ceg', 'r': 'eg', 't': 'defg'
  };
  var LCD_DAYS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'];
  var LCD_MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

  function two(n) { return ('0' + n).slice(-2); }

  function bindLcd(root) {
    if (bound.has(root)) return;
    bound.add(root);

    var screen = root.querySelector('[data-ranger-lcd-screen]');
    var digits = Array.prototype.slice.call(root.querySelectorAll('[data-ranger-lcd-digit]'));
    var colons = Array.prototype.slice.call(root.querySelectorAll('[data-ranger-lcd-colon]'));
    var buttons = Array.prototype.slice.call(root.querySelectorAll('[data-ranger-lcd-btn]'));
    var container = root.closest('.ranger-lp-page') || document;
    var modeBtns = Array.prototype.slice.call(container.querySelectorAll('[data-ranger-lcd-mode-btn]'));
    if (!digits.length || !modeBtns.length) return;

    var out = {
      mode: root.querySelector('[data-ranger-lcd-mode]'),
      bottom: root.querySelector('[data-ranger-lcd-bottom]'),
      note: root.querySelector('[data-ranger-lcd-note]'),
      hintL: root.querySelector('[data-ranger-lcd-hint-left]'),
      hintR: root.querySelector('[data-ranger-lcd-hint-right]'),
      status: root.querySelector('[data-ranger-lcd-status]')
    };
    var indicators = {};
    var indEls = root.querySelectorAll('[data-ranger-lcd-ind]');
    for (var n = 0; n < indEls.length; n++) indicators[indEls[n].getAttribute('data-ranger-lcd-ind')] = indEls[n];

    /* Cache the segment elements once: querying 42 nodes per frame is the kind
       of thing that makes a 1/100 counter drop frames on a cheap phone. */
    var cells = digits.map(function (d) {
      var map = {};
      'abcdefg'.split('').forEach(function (k) {
        map[k] = d.querySelector('.ranger-lp__seg--' + k);
      });
      return map;
    });

    var current = 0;
    var state = { al: true, chm: false, h24: true, run: false, t0: 0, acc: 0, split: null };
    var lightTimer = null;
    var ticker = null;
    var frame = null;
    var visible = false;
    /* Only write to the DOM when the value changed — the live region in
       particular must not be rewritten on every tick. */
    var last = {};

    function put(el, key, value) {
      if (!el || last[key] === value) return;
      last[key] = value;
      el.textContent = value;
    }

    function paint(str) {
      for (var i = 0; i < cells.length; i++) {
        var ch = str.charAt(i) || ' ';
        var on = SEGMENTS[ch];
        if (on === undefined) on = SEGMENTS[ch.toUpperCase()] || '';
        var keys = 'abcdefg';
        for (var k = 0; k < keys.length; k++) {
          var seg = cells[i][keys.charAt(k)];
          if (seg) seg.classList.toggle('is-on', on.indexOf(keys.charAt(k)) > -1);
        }
      }
    }

    function setColons(show, blink) {
      for (var i = 0; i < colons.length; i++) {
        colons[i].style.visibility = show ? 'visible' : 'hidden';
        colons[i].classList.toggle('is-blinking', !!blink);
      }
    }

    function elapsed() { return state.acc + (state.run ? Date.now() - state.t0 : 0); }
    function data(btn, key) { return btn.getAttribute('data-' + key) || ''; }

    function render() {
      var btn = modeBtns[current];
      var key = btn.getAttribute('data-mode-key');
      var now = new Date();
      var bottom = data(btn, 'bottom');
      var note = '';

      if (key === 'time') {
        var h = now.getHours();
        if (!state.h24) h = h % 12 || 12;
        setColons(true, true);
        paint(two(h) + two(now.getMinutes()) + two(now.getSeconds()));
        bottom = LCD_DAYS[now.getDay()] + ' ' + two(now.getDate()) + ' ' + LCD_MONTHS[now.getMonth()];
        note = state.h24 ? '24 H' : '12 H';
      } else if (key === 'alarm') {
        setColons(true, false);
        paint('0530  ');
        note = state.al ? data(btn, 'note-on') : data(btn, 'note-off');
      } else if (key === 'chrono') {
        var e = elapsed();
        setColons(true, false);
        paint(two(Math.floor(e / 60000) % 60) + two(Math.floor(e / 1000) % 60) + two(Math.floor(e / 10) % 100));
        bottom = state.split != null
          ? 'SPLIT ' + two(Math.floor(state.split / 60000) % 60) + ':' + two(Math.floor(state.split / 1000) % 60)
          : (bottom || 'MIN SEC 1/100');
        note = state.run ? 'RUNNING' : (e > 0 ? 'STOPPED' : 'READY');
      } else if (key === 'day') {
        setColons(false, false);
        paint(two(now.getDate()) + '    ');
        bottom = LCD_DAYS[now.getDay()];
        note = LCD_MONTHS[now.getMonth()] + ' ' + now.getFullYear();
      } else {
        setColons(true, false);
        paint('  00  ');
        note = state.chm ? data(btn, 'note-on') : data(btn, 'note-off');
      }

      put(out.mode, 'mode', data(btn, 'mode-label'));
      put(out.bottom, 'bottom', bottom);
      put(out.note, 'note', note);
      put(out.hintL, 'hintL', data(btn, 'hint-left'));
      put(out.hintR, 'hintR', data(btn, 'hint-right'));
      /* Words, not segments — and only when the wording actually changes. */
      put(out.status, 'status', data(btn, 'mode-label') + '. ' + bottom + '. ' + note + '.');

      if (indicators.al) indicators.al.classList.toggle('is-on', state.al);
      if (indicators.chm) indicators.chm.classList.toggle('is-on', state.chm);
      if (indicators.sw) indicators.sw.classList.toggle('is-on', key === 'chrono');
      if (indicators.h24) indicators.h24.classList.toggle('is-on', state.h24);

      for (var m = 0; m < modeBtns.length; m++) {
        modeBtns[m].setAttribute('aria-pressed', m === current ? 'true' : 'false');
      }
    }

    /* A running chronograph wants every frame; nothing else needs more than a
       few ticks a second. Both are torn down the moment the band is hidden. */
    function schedule() {
      stop();
      if (!visible) return;
      if (modeBtns[current].getAttribute('data-mode-key') === 'chrono' && state.run) {
        var loop = function () { render(); frame = window.requestAnimationFrame(loop); };
        frame = window.requestAnimationFrame(loop);
      } else {
        ticker = window.setInterval(render, 250);
      }
    }

    function stop() {
      if (ticker) { window.clearInterval(ticker); ticker = null; }
      if (frame) { window.cancelAnimationFrame(frame); frame = null; }
    }

    function press(which) {
      var key = modeBtns[current].getAttribute('data-mode-key');
      if (which === 'light') {
        if (screen) {
          screen.classList.add('is-lit');
          window.clearTimeout(lightTimer);
          lightTimer = window.setTimeout(function () { screen.classList.remove('is-lit'); }, 4000);
        }
      } else if (which === 'mode') {
        current = (current + 1) % modeBtns.length;
        state.split = null;
      } else if (which === 'start') {
        if (key === 'chrono') {
          if (state.run) { state.acc = elapsed(); state.run = false; }
          else { state.t0 = Date.now(); state.run = true; }
        } else {
          for (var i = 0; i < modeBtns.length; i++) {
            if (modeBtns[i].getAttribute('data-mode-key') === 'chrono') { current = i; break; }
          }
        }
      } else {
        if (key === 'chrono') {
          if (state.run) state.split = elapsed();
          else { state.acc = 0; state.split = null; }
        } else if (key === 'alarm') state.al = !state.al;
        else if (key === 'chime') state.chm = !state.chm;
        else if (key === 'time') state.h24 = !state.h24;
      }
      render();
      schedule();
    }

    function onPusher(event) {
      var el = event.currentTarget;
      el.classList.add('is-pressed');
      window.setTimeout(function () { el.classList.remove('is-pressed'); }, 110);
      press(el.getAttribute('data-ranger-lcd-btn'));
    }

    function onMode(event) {
      current = Number(event.currentTarget.getAttribute('data-ranger-lcd-mode-btn'));
      state.split = null;
      render();
      schedule();
    }

    for (var b = 0; b < buttons.length; b++) buttons[b].addEventListener('click', onPusher);
    for (var mb = 0; mb < modeBtns.length; mb++) modeBtns[mb].addEventListener('click', onMode);

    render();

    var observer = null;
    if (window.IntersectionObserver) {
      observer = new IntersectionObserver(function (entries) {
        visible = entries[0].isIntersecting;
        schedule();
      }, { rootMargin: '120px' });
      observer.observe(root);
    } else {
      visible = true;
      schedule();
    }

    function onVisibility() {
      if (document.hidden) stop(); else schedule();
    }
    document.addEventListener('visibilitychange', onVisibility);

    onUnload(root, function () {
      stop();
      window.clearTimeout(lightTimer);
      if (observer) observer.disconnect();
      document.removeEventListener('visibilitychange', onVisibility);
      for (var i = 0; i < buttons.length; i++) buttons[i].removeEventListener('click', onPusher);
      for (var m = 0; m < modeBtns.length; m++) modeBtns[m].removeEventListener('click', onMode);
    });
  }

  /* -------------------------------------------------------------------------
     v2 band 03 — after dark

     Sets one custom property on the stage and writes the caption. The light
     curve itself lives in CSS, so how fast night falls is a styling decision.

     aria-valuetext carries the caption, so a screen reader hears
     "Dusk · 60%" instead of "60".
     ------------------------------------------------------------------------- */
  function bindLume(root) {
    if (bound.has(root)) return;
    bound.add(root);

    var range = root.querySelector('[data-ranger-lume-range]');
    var stage = root.querySelector('[data-ranger-lume-stage]');
    var stamp = root.querySelector('[data-ranger-lume-stamp]');
    if (!range || !stage) return;

    var dayLabel = root.getAttribute('data-label-day') || 'Daylight';
    var duskLabel = root.getAttribute('data-label-dusk') || 'Dusk';
    var nightLabel = root.getAttribute('data-label-night') || 'Lume · charged';

    function update() {
      var v = Number(range.value) / 100;
      stage.style.setProperty('--ranger-lume', String(v));
      var light = Math.round((1 - v) * 100);
      var text = v < 0.15 ? dayLabel + ' · ' + light + '%'
        : v < 0.6 ? duskLabel + ' · ' + light + '%'
        : nightLabel;
      if (stamp) stamp.textContent = text;
      range.setAttribute('aria-valuetext', text);
    }

    range.addEventListener('input', update);
    update();

    onUnload(root, function () {
      range.removeEventListener('input', update);
    });
  }

  var ROOTS = [
    ['[data-ranger-lume]', bindLume],
    ['[data-ranger-lcd]', bindLcd],
    ['[data-ranger-anatomy]', bindAnatomy],
    ['[data-ranger-scroll-to-form]', bindScrollToForm],
    ['[data-ranger-signup][data-mode="klaviyo"]', bindSignup],
    ['[data-ranger-model]', bindModel],
    ['[data-ranger-persistent-signup]', bindPersistent],
    ['[data-ranger-video]', bindVideo]
  ];

  function boot(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    for (var i = 0; i < ROOTS.length; i++) {
      var found = context.querySelectorAll(ROOTS[i][0]);
      for (var f = 0; f < found.length; f++) ROOTS[i][1](found[f]);
    }
  }

  function unboot(scope) {
    if (!scope || !scope.querySelectorAll) return;
    for (var i = 0; i < ROOTS.length; i++) {
      var found = scope.querySelectorAll(ROOTS[i][0]);
      for (var f = 0; f < found.length; f++) {
        var root = found[f];
        var list = teardown.get(root);
        if (list) {
          for (var t = 0; t < list.length; t++) list[t]();
          teardown.delete(root);
        }
        bound.delete(root);
      }
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { boot(document); });
  } else {
    boot(document);
  }

  /* Theme Editor lifecycle. A reloaded section may contain the persistent bar
     or a landmark another section's observer depends on, so rebind the whole
     document after a load rather than only the reloaded subtree. */
  document.addEventListener('shopify:section:load', function (event) {
    boot(event.target);
    var bar = document.querySelector('[data-ranger-persistent-signup]');
    if (bar) {
      var list = teardown.get(bar);
      if (list) {
        for (var t = 0; t < list.length; t++) list[t]();
        teardown.delete(bar);
      }
      bound.delete(bar);
      bindPersistent(bar);
    }
  });

  document.addEventListener('shopify:section:unload', function (event) {
    unboot(event.target);
  });

  /* Selecting a colourway block in the editor should show that colourway. */
  document.addEventListener('shopify:block:select', function (event) {
    var block = event.target;
    if (!block) return;
    var key = block.getAttribute && block.getAttribute('data-variant-key');
    var model = block.closest && block.closest('[data-ranger-model]');
    if (!key || !model) return;
    var button = model.querySelector('[data-ranger-variant-button][data-variant-key="' + key + '"]');
    if (button) button.click();
  });
})();
